import { telemetry } from '../../../@vendor/Posthog';
import { Client, ClientManager } from '../../../concepts/objects';
import { Line } from '../../../platform/geom/Line';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { MouseInput } from '../../../platform/input/MouseInput';
import { resolve } from '../../Container';
import { Camera } from '../Camera';
import { SlapRelay } from './SlapRelay';
import { ToolBehavior } from './ToolBehavior';

type Sample = [Point, number];
const now = () => performance.now();

export class SlapToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera: Camera;
    @resolve(ClientManager) private clients: ClientManager;
    @resolve(SlapRelay) private relay: SlapRelay;

    private buffer = [] as Sample[];
    private tracker = new Map<string, number>();

    public onMouseMove(state: string, e: MouseInput) {
        if (state === 'panningPrimed') {
            this.buffer.push([this.camera.toModel(e.position), now()]);
            if (this.buffer.length > 6) {
                this.buffer.splice(0, this.buffer.length - 6);
            }
            this.performTick();
        }
        else if (this.buffer.length) {
            this.buffer = [];
        }
    }

    private performTick() {
        const candidates = this.getSlapCandidates();
        for (const client of candidates) {
            const [hit, vec, str] = this.testClientForSlap(client);
            if (hit) {
                this.dispatchSlap(client, vec, str);
            }
        }
    }

    private getSlapCandidates() {
        const area = Rect.fromPoints(this.buffer.map(x => x[0]));
        return this.clients.others.filter(c => {
            // If slapped recently, ignore
            const last = this.tracker.get(c.id);
            if (!!last && (now() - last) < 500) return false;
            // Otherwise, is it within the slap area?
            const x = c.get('px');
            if (typeof (x) !== 'number') return false;
            const y = c.get('py');
            if (typeof (y) !== 'number') return false;
            return area.contains({ x, y });
        });
    }

    private testClientForSlap(client: Client) {
        const cp = new Point(client.get('px') as number, client.get('py') as number);
        for (let i = 1; i < this.buffer.length; i++) {

            const ab = new Line(this.buffer[i - 1][0], this.buffer[i][0]);
            if (ab.distanceTo(cp) > 20) continue;

            // Slap detected, so measure strength
            const slice = this.buffer.slice(0, i + 1);
            const ts = slice.map((x, xi) => xi === 0 ? 0 : x[1] - slice[xi - 1][1]).reduce((a, x) => a + x, 0);
            const ds = Polyline.from(this.buffer.slice(0, i + 1).map(x => x[0])).length;
            const s = ds / ts;

            // If strong enough, success
            if (s >= 2.5) {
                return [true, ab.toVector().normalize(), s] as const;
            }
        }
        return [false] as const;
    }

    private dispatchSlap(client: Client, vector: Point, power: number) {
        this.tracker.set(client.id, now());
        this.relay.emit('slap', [client.id, vector, power]);
        telemetry.emit('interaction.slap');
    }
}
